import {OverflowList} from "@theme/critical";
import VariantPicker from "@theme/variant-picker";
import {Component} from "@theme/component";
import {debounce, isDesktopBreakpoint, mediaQueryLarge, requestYieldCallback} from "@theme/utilities";
import {ThemeEvents, SlideshowSelectEvent} from "@theme/events";
import {morph} from "@theme/morph";

/**
 * A custom element that displays a product card.
 *
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} productCardLink - The product card link element.
 * @property {import('slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {import('quick-add').QuickAddComponent} [quickAdd] - The quick add component.
 * @property {HTMLElement} [cardGallery] - The card gallery component.
 *
 * @extends {Component<Refs>}
 */
export class ProductCard extends Component {
	requiredRefs = ["productCardLink"];

	/** @type {MutationObserver | undefined} */
	#intendedVariantGuard;

	/** @type {MutationObserver | undefined} */
	#priceGuard;

	/** @type {Map<HTMLElement, string> | undefined} */
	#intendedPriceHtmls;

	/** @type {string | undefined} */
	#priceGuardLastVariantId;

	get productPageUrl() {
		return this.refs.productCardLink.href;
	}

	/**
	 * Gets the currently selected variant ID from the product card
	 * @returns {string | null} The variant ID or null if none selected
	 */
	getSelectedVariantId() {
		const checkedInput = /** @type {HTMLInputElement | null} */ (
			this.querySelector('input[type="radio"]:checked[data-variant-id]')
		);

		return checkedInput?.dataset.variantId || null;
	}

	/**
	 * Gets the product card link element
	 * @returns {HTMLAnchorElement | null} The product card link or null
	 */
	getProductCardLink() {
		return this.refs.productCardLink || null;
	}

	#fetchProductPageHandler = () => {
		this.refs.quickAdd?.fetchProductPage(this.productPageUrl);
	};

	/**
	 * Navigates to a URL link. Respects modifier keys for opening in new tab/window.
	 * @param {Event} event - The event that triggered the navigation.
	 * @param {URL} url - The URL to navigate to.
	 */
	#navigateToURL = (event, url) => {
		// Check for modifier keys that should open in new tab/window (only for mouse events)
		const shouldOpenInNewTab =
			event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.button === 1);

		if (shouldOpenInNewTab) {
			event.preventDefault();
			window.open(url.href, "_blank");
			return;
		} else {
			window.location.href = url.href;
		}
	};

	connectedCallback() {
		super.connectedCallback();

		const link = this.refs.productCardLink;
		if (!(link instanceof HTMLAnchorElement)) throw new Error("Product card link not found");

		// Defend the SSR-resolved variant ID against third-party scripts (e.g. the
		// b4b-variable-products Shopify app) that overwrite product-card link hrefs
		// with a non-filter-aware variant ID. The intended variant ID comes from
		// Liquid's filter-aware logic and is stored on `data-intended-variant-id`.
		// We update `data-intended-variant-id` ourselves whenever a legitimate variant
		// change occurs (see `#updateProductUrl`), so the guard never reverts our own changes.
		/** @param {Element | undefined} anchor */
		const enforceIntendedVariantOn = anchor => {
			const intendedVariantId = this.dataset.intendedVariantId;
			if (!intendedVariantId || !(anchor instanceof HTMLAnchorElement)) return;
			const href = anchor.getAttribute("href");
			if (!href || !href.trim()) return;
			const url = new URL(anchor.href, location.origin);
			const currentVariant = url.searchParams.get("variant");
			if (currentVariant === intendedVariantId) return;
			url.searchParams.set("variant", intendedVariantId);
			anchor.setAttribute("href", url.pathname + url.search);
		};
		const enforceIntendedVariant = () => {
			enforceIntendedVariantOn(link);
			const {cardGalleryLink, productTitleLink} = this.refs;
			if (cardGalleryLink instanceof HTMLAnchorElement) enforceIntendedVariantOn(cardGalleryLink);
			if (productTitleLink instanceof HTMLAnchorElement) enforceIntendedVariantOn(productTitleLink);
		};
		this.#intendedVariantGuard?.disconnect();
		this.#intendedVariantGuard = new MutationObserver(enforceIntendedVariant);
		this.#intendedVariantGuard.observe(this, {
			attributes: true,
			attributeFilter: ["href"],
			subtree: true,
		});
		// Correct any pre-existing wrong hrefs (e.g. when b4b set href, disconnected,
		// then reconnected the card synchronously — queued mutation records were discarded).
		enforceIntendedVariant();

		// Defend the SSR-rendered price HTML against the same third-party app that
		// rewrites price spans (e.g. adding `b4b-vprice` / `b4b-compare-at-price`
		// classes and replacing prices with the first variant's values during its
		// disconnect/reconnect cycle). The intended price HTML is captured once on
		// first connect (when the SSR markup is fresh) and re-asserted whenever the
		// price subtree is mutated externally. Legitimate price changes (section
		// morph after filter change, swatch-driven variant updates) update the
		// intended HTML so the guard accepts the new price.
		// A product card can contain multiple <product-price> elements (e.g. the
		// nova-product-card-gallery block renders a hidden zoom-out price, and the
		// nova-price block renders the main visible price). We must guard ALL of
		// them: b4b mutates whichever ones are visible, and we previously only
		// protected the first one in DOM order (which is the hidden zoom-out copy).
		const priceContainers = /** @type {HTMLElement[]} */ (
			Array.from(this.querySelectorAll('product-price [ref="priceContainer"]')).filter(
				el => el instanceof HTMLElement,
			)
		);
		if (priceContainers.length > 0) {
			if (this.#intendedPriceHtmls === undefined) {
				this.#intendedPriceHtmls = new Map();
				for (const pc of priceContainers) this.#intendedPriceHtmls.set(pc, pc.innerHTML);
				this.#priceGuardLastVariantId = this.dataset.intendedVariantId;
			} else {
				for (const pc of priceContainers) {
					const intended = this.#intendedPriceHtmls.get(pc);
					if (intended === undefined) {
						this.#intendedPriceHtmls.set(pc, pc.innerHTML);
					} else if (pc.innerHTML !== intended) {
						pc.innerHTML = intended;
						this.#intendedPriceHtmls.set(pc, pc.innerHTML);
					}
				}
			}

			this.#priceGuard?.disconnect();
			this.#priceGuard = new MutationObserver(records => {
				if (!this.#intendedPriceHtmls) return;

				// A section morph (e.g. filter change) updates `data-intended-variant-id`
				// alongside the price. Detect that and accept the new prices as truth.
				const currentVariantId = this.dataset.intendedVariantId;
				if (currentVariantId !== this.#priceGuardLastVariantId) {
					this.#priceGuardLastVariantId = currentVariantId;
					for (const pc of this.#intendedPriceHtmls.keys()) {
						if (pc.isConnected) this.#intendedPriceHtmls.set(pc, pc.innerHTML);
					}
					return;
				}

				const mutated = new Set();
				for (const r of records) {
					let n = r.target instanceof Element ? r.target : null;
					while (n) {
						if (n instanceof HTMLElement && this.#intendedPriceHtmls.has(n)) {
							mutated.add(n);
							break;
						}
						n = n.parentElement;
					}
				}
				for (const pc of mutated) {
					const el = /** @type {HTMLElement} */ (pc);
					const intended = this.#intendedPriceHtmls.get(el);
					if (intended === undefined || el.innerHTML === intended) continue;
					el.innerHTML = intended;
					this.#intendedPriceHtmls.set(el, el.innerHTML);
				}
			});
			for (const pc of priceContainers) {
				this.#priceGuard.observe(pc, {childList: true, subtree: true, characterData: true});
			}
		}

		this.#handleQuickAdd();

		this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
		this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
		this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSlideshowSelect);
		mediaQueryLarge.addEventListener("change", this.#handleQuickAdd);

		this.addEventListener("click", this.navigateToProduct);

		// Preload the next image on the slideshow to avoid white flashes on previewImage
		setTimeout(() => {
			if (this.refs.slideshow?.isNested) {
				this.#preloadNextPreviewImage();
			}
		});
	}

	disconnectedCallback() {
		super.disconnectedCallback();
		this.removeEventListener("click", this.navigateToProduct);
		this.#intendedVariantGuard?.disconnect();
		this.#intendedVariantGuard = undefined;
		this.#priceGuard?.disconnect();
		this.#priceGuard = undefined;
	}

	#preloadNextPreviewImage() {
		const currentSlide = this.refs.slideshow?.slides?.[this.refs.slideshow?.current];
		currentSlide?.nextElementSibling?.querySelector('img[loading="lazy"]')?.removeAttribute("loading");
	}

	/**
	 * Handles the quick add event.
	 */
	#handleQuickAdd = () => {
		this.removeEventListener("pointerenter", this.#fetchProductPageHandler);
		this.removeEventListener("focusin", this.#fetchProductPageHandler);

		if (isDesktopBreakpoint()) {
			this.addEventListener("pointerenter", this.#fetchProductPageHandler);
			this.addEventListener("focusin", this.#fetchProductPageHandler);
		}
	};

	/**
	 * Handles the variant selected event.
	 * @param {VariantSelectedEvent} event - The variant selected event.
	 */
	#handleVariantSelected = event => {
		if (event.target !== this.variantPicker) {
			this.variantPicker?.updateSelectedOption(event.detail.resource.id);
		}
	};

	/**
	 * Handles the variant update event.
	 * Updates price, checks for unavailable variants, and updates product URL.
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	#handleVariantUpdate = event => {
		// Stop the event from bubbling up to the section, variant updates triggered from product cards are fully handled
		// by this component and should not affect anything outside the card.
		event.stopPropagation();

		this.updatePrice(event);
		this.#isUnavailableVariantSelected(event);
		this.#updateProductUrl(event);
		this.refs.quickAdd?.fetchProductPage(this.productPageUrl);

		if (event.target !== this.variantPicker) {
			this.variantPicker?.updateVariantPicker(event.detail.data.html);
		}

		this.#updateVariantImages();
		this.#previousSlideIndex = null;
		this.#imageHoverRestoreIndex = null;

		// Remove attribute after re-rendering since a variant selection has been made
		this.removeAttribute("data-no-swatch-selected");

		// Force overflow list to reflow after variant update
		// This fixes an issue where the overflow counter doesn't update properly in some browsers
		this.#updateOverflowList();
	};

	/**
	 * Forces the overflow list to recalculate by dispatching a reflow event.
	 * This ensures the overflow counter displays correctly after variant updates.
	 */
	#updateOverflowList() {
		// Find the overflow list in the variant picker
		const overflowList = this.querySelector("swatches-variant-picker-component overflow-list");
		const isActiveOverflowList = overflowList?.querySelector('[slot="overflow"]') ? true : false;
		if (!overflowList || !isActiveOverflowList) return;

		// Use requestAnimationFrame to ensure DOM has been updated
		requestAnimationFrame(() => {
			// Dispatch a reflow event to trigger recalculation
			overflowList.dispatchEvent(
				new CustomEvent("reflow", {
					bubbles: true,
					detail: {},
				}),
			);
		});
	}

	/**
	 * Updates the DOM with a new price.
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	updatePrice(event) {
		const allContainers = this.querySelectorAll(`product-price [ref='priceContainer']`);
		const newContainers = event.detail.data.html.querySelectorAll(`product-price [ref='priceContainer']`);
		const nOld = allContainers.length;
		const nNew = newContainers.length;

		if (!nNew || !nOld) return;

		const targetDoc = this.ownerDocument;

		if (nNew === nOld) {
			for (let i = 0; i < nOld; i++) {
				const oldEl = allContainers.item(i);
				const imported = targetDoc.importNode(/** @type {Node} */ (newContainers.item(i)), true);
				if (oldEl instanceof HTMLElement && imported instanceof HTMLElement) morph(oldEl, imported);
			}
		} else if (nNew < nOld) {
			const template = newContainers.item(0);
			if (!(template instanceof Element)) return;
			for (let i = 0; i < nOld; i++) {
				const oldEl = allContainers.item(i);
				const imported = targetDoc.importNode(template, true);
				if (oldEl instanceof HTMLElement && imported instanceof HTMLElement) morph(oldEl, imported);
			}
		} else {
			for (let i = 0; i < nOld; i++) {
				const oldEl = allContainers.item(i);
				const imported = targetDoc.importNode(/** @type {Node} */ (newContainers.item(i)), true);
				if (oldEl instanceof HTMLElement && imported instanceof HTMLElement) morph(oldEl, imported);
			}
		}

		// Sync intended price HTML for every guarded container so the price guard
		// accepts this legitimate update instead of reverting it on the next tick.
		if (this.#intendedPriceHtmls) {
			const refreshed = this.querySelectorAll('product-price [ref="priceContainer"]');
			for (const pc of refreshed) {
				if (pc instanceof HTMLElement) this.#intendedPriceHtmls.set(pc, pc.innerHTML);
			}
			this.#priceGuardLastVariantId = this.dataset.intendedVariantId;
		}
	}

	/**
	 * Updates the product URL based on the variant update event.
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	#updateProductUrl(event) {
		const anchorElement = event.detail.data.html?.querySelector("product-card a");
		const featuredMediaUrl = event.detail.data.html
			?.querySelector("product-card-link")
			?.getAttribute("data-featured-media-url");

		// If the product card is inside a product link, update the product link's featured media URL
		if (featuredMediaUrl && this.closest("product-card-link"))
			this.closest("product-card-link")?.setAttribute("data-featured-media-url", featuredMediaUrl);

		if (anchorElement instanceof HTMLAnchorElement) {
			// If the href is empty, don't update the product URL eg: unavailable variant
			if (anchorElement.getAttribute("href")?.trim() === "") return;

			const productUrl = anchorElement.href;
			const {productCardLink, productTitleLink, cardGalleryLink} = this.refs;

			// Update the intended variant ID first so the guard does not revert this legitimate change.
			try {
				const newVariantId = new URL(productUrl, location.origin).searchParams.get("variant");
				if (newVariantId) this.dataset.intendedVariantId = newVariantId;
			} catch {}

			productCardLink.href = productUrl;
			if (cardGalleryLink instanceof HTMLAnchorElement) {
				cardGalleryLink.href = productUrl;
			}
			if (productTitleLink instanceof HTMLAnchorElement) {
				productTitleLink.href = productUrl;
			}
		}
	}

	/**
	 * Checks if an unavailable variant is selected.
	 * @param {VariantUpdateEvent} event - The variant update event.
	 */
	#isUnavailableVariantSelected(event) {
		const allVariants = /** @type {NodeListOf<HTMLInputElement>} */ (
			event.detail.data.html.querySelectorAll("input:checked")
		);

		for (const variant of allVariants) {
			this.#toggleAddToCartButton(variant.dataset.optionAvailable === "true");
		}
	}

	/**
	 * Toggles the add to cart button state.
	 * @param {boolean} enable - Whether to enable or disable the button.
	 */
	#toggleAddToCartButton(enable) {
		const addToCartButton = this.querySelector(".add-to-cart__button button");

		if (addToCartButton instanceof HTMLButtonElement) {
			addToCartButton.disabled = !enable;
		}
	}

	/**
	 * Hide the variant images that are not for the selected variant.
	 */
	#updateVariantImages() {
		const {slideshow} = this.refs;
		if (!this.variantPicker?.selectedOption) {
			return;
		}

		const selectedImageId = this.variantPicker?.selectedOption.dataset.optionMediaId;

		if (slideshow && selectedImageId) {
			const {slides = []} = slideshow.refs;

			for (const slide of slides) {
				if (slide.getAttribute("variant-image") == null) continue;

				slide.hidden = slide.getAttribute("slide-id") !== selectedImageId;
			}

			slideshow.select({id: selectedImageId}, undefined, {animate: false});
		}
	}

	/**
	 * Gets all variant inputs.
	 * @returns {NodeListOf<HTMLInputElement>} All variant input elements.
	 */
	get allVariants() {
		return this.querySelectorAll("input[data-variant-id]");
	}

	/**
	 * Gets the variant picker component.
	 * @returns {VariantPicker | null} The variant picker component.
	 */
	get variantPicker() {
		return this.querySelector("swatches-variant-picker-component");
	}
	/** @type {number | null} */
	#previousSlideIndex = null;

	/** Slide index to restore after gallery "second image on hover" when a swatch picker is present. */
	#imageHoverRestoreIndex = null;

	/**
	 * Handles the slideshow select event.
	 * @param {SlideshowSelectEvent} event - The slideshow select event.
	 */
	#handleSlideshowSelect = event => {
		if (event.detail.userInitiated) {
			this.#previousSlideIndex = event.detail.index;
		}
	};

	/**
	 * Previews a variant.
	 * @param {string} id - The id of the variant to preview.
	 */
	previewVariant(id) {
		const {slideshow} = this.refs;

		if (!slideshow) return;

		this.resetVariant.cancel();
		this.#imageHoverRestoreIndex = null;
		slideshow.select({id}, undefined, {animate: false});
	}

	/**
	 * Previews the next image.
	 * @param {PointerEvent} event - The pointer event.
	 */
	previewImage(event) {
		if (event.pointerType !== "mouse") return;

		const {slideshow} = this.refs;

		if (!slideshow) return;

		this.resetVariant.cancel();

		this.#imageHoverRestoreIndex = slideshow.current;

		if (this.#previousSlideIndex != null && this.#previousSlideIndex > 0) {
			slideshow.select(this.#previousSlideIndex, undefined, {animate: false});
		} else {
			slideshow.next(undefined, {animate: false});
			setTimeout(() => this.#preloadNextPreviewImage());
		}
	}

	/**
	 * Resets the image to the variant image.
	 * @param {PointerEvent} event - The pointer event.
	 */
	resetImage(event) {
		if (event.pointerType !== "mouse") return;

		const {slideshow} = this.refs;

		if (!this.variantPicker) {
			if (!slideshow) return;
			slideshow.previous(undefined, {animate: false});
		} else if (this.#imageHoverRestoreIndex != null) {
			if (slideshow) {
				slideshow.select(this.#imageHoverRestoreIndex, undefined, {animate: false});
			}
			this.#imageHoverRestoreIndex = null;
		} else {
			this.#resetVariant();
		}
	}

	/**
	 * Resets the image to the variant image.
	 */
	#resetVariant = () => {
		const {slideshow} = this.refs;

		if (!slideshow) return;

		// If we have a selected variant, always use its image
		if (this.variantPicker?.selectedOption) {
			const id = this.variantPicker.selectedOption.dataset.optionMediaId;
			if (id) {
				slideshow.select({id}, undefined, {animate: false});
				return;
			}
		}

		// No variant selected - use initial slide if it's valid
		const initialSlide = slideshow.initialSlide;
		const slideId = initialSlide?.getAttribute("slide-id");
		if (initialSlide && slideshow.slides?.includes(initialSlide) && slideId) {
			slideshow.select({id: slideId}, undefined, {animate: false});
			return;
		}

		// No valid initial slide or selected variant - go to previous
		slideshow.previous(undefined, {animate: false});
	};

	/**
	 * Intercepts the click event on the product card anchor, we want
	 * to use this to add an intermediate state to the history.
	 * This intermediate state captures the page we were on so that we
	 * navigate back to the same page when the user navigates back.
	 * In addition to that, it captures the product card anchor so that we
	 * have the specific product card in view.
	 *
	 * A product card can have other interactive elements like variant picker,
	 * so we do not navigate if the click was on one of those elements.
	 *
	 * @param {Event} event
	 */
	navigateToProduct = event => {
		if (!(event.target instanceof Element)) return;

		// Don't navigate if this product card is marked as no-navigation (e.g., in theme editor)
		if (this.hasAttribute("data-no-navigation")) return;

		const interactiveElement = event.target.closest('button, input, label, select, [tabindex="1"]');

		// If the click was on an interactive element, do nothing.
		if (interactiveElement) {
			return;
		}

		const link = this.refs.productCardLink;
		if (!link.href) return;
		const linkURL = new URL(link.href);

		const productCardAnchor = link.getAttribute("id");
		if (!productCardAnchor) return;

		const url = new URL(window.location.href);
		const parent = this.closest("li");
		url.hash = productCardAnchor;
		if (parent && parent.dataset.page) {
			url.searchParams.set("page", parent.dataset.page);
		}

		if (!window.Shopify.designMode) {
			requestYieldCallback(() => {
				history.replaceState({}, "", url.toString());
			});
		}

		const targetLink = event.target.closest("a");
		// Let the native navigation handle the click if it was on a link.
		if (!targetLink) {
			this.#navigateToURL(event, linkURL);
		}
	};

	/**
	 * Resets the variant.
	 */
	resetVariant = debounce(this.#resetVariant, 100);
}

if (!customElements.get("product-card")) {
	customElements.define("product-card", ProductCard);
}

/**
 * A custom element that displays a variant picker with swatches.
 *
 * @typedef {object} SwatchesRefs
 * @property {HTMLElement} overflowList
 *
 * @extends {VariantPicker<SwatchesRefs>}
 */
class SwatchesVariantPickerComponent extends VariantPicker {
	connectedCallback() {
		super.connectedCallback();

		// Cache the parent product card
		this.parentProductCard = this.closest("product-card");

		// Listen for variant updates to apply pending URL changes
		this.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUrlUpdate.bind(this));
	}

	/**
	 * Updates the card URL when a variant is selected.
	 */
	#handleCardVariantUrlUpdate() {
		if (this.pendingVariantId && this.parentProductCard instanceof ProductCard) {
			const currentUrl = new URL(this.parentProductCard.refs.productCardLink.href);
			currentUrl.searchParams.set("variant", this.pendingVariantId);
			// Update the intended variant ID first so the card's guard does not revert this legitimate change.
			this.parentProductCard.dataset.intendedVariantId = this.pendingVariantId;
			this.parentProductCard.refs.productCardLink.href = currentUrl.toString();
			this.pendingVariantId = null;
		}
	}

	/**
	 * Handles swatch click events to redirect to the product page with the correct variant.
	 * @param {Event} event - The click event.
	 */
	handleSwatchClick(event) {
		event.preventDefault();
		event.stopPropagation();

		// Get the input element (either the target itself or the closest input)
		const input =
			event.target instanceof HTMLInputElement
				? event.target
				: event.target.closest("label")?.querySelector('input[type="radio"]');

		if (!input) return;

		const variantId = input.dataset.variantId || input.dataset.firstAvailableOrFirstVariantId;
		const productUrl = this.dataset.productUrl?.split("?")[0];

		if (!productUrl || !variantId) return;

		// Build the product URL with the variant parameter
		const url = new URL(productUrl, window.location.origin);
		url.searchParams.set("variant", variantId);

		// Navigate to the product page with the selected variant
		window.location.href = url.href;
	}

	/**
	 * Override the variantChanged method to handle unavailable swatches with available alternatives.
	 * @param {Event} event - The variant change event.
	 */
	variantChanged(event) {
		if (!(event.target instanceof HTMLElement)) return;

		// Check if this is a swatch input
		const isSwatchInput = event.target instanceof HTMLInputElement && event.target.name?.includes("-swatch");
		const clickedSwatch = event.target;
		const availableCount = parseInt(clickedSwatch.dataset.availableCount || "0");
		const firstAvailableVariantId = clickedSwatch.dataset.firstAvailableOrFirstVariantId;

		// For swatch inputs, check if we need special handling
		if (isSwatchInput && availableCount > 0 && firstAvailableVariantId) {
			// If this is an unavailable variant but there are available alternatives
			// Prevent the default handling
			event.stopPropagation();

			// Update the selected option visually
			this.updateSelectedOption(clickedSwatch);

			// Build request URL with the first available variant
			const productUrl = this.dataset.productUrl?.split("?")[0];

			if (!productUrl) return;

			const url = new URL(productUrl, window.location.origin);
			url.searchParams.set("variant", firstAvailableVariantId);
			// Check if we're in a nova product card
			const isNovaCard = this.hasAttribute("data-nova");
			const sectionId = isNovaCard ? "nova-section-rendering-product-card" : "section-rendering-product-card";
			url.searchParams.set("section_id", sectionId);

			const requestUrl = url.href;

			// Store the variant ID we want to apply to the URL
			this.pendingVariantId = firstAvailableVariantId;

			// Use parent's fetch method
			this.fetchUpdatedSection(requestUrl);
			return;
		}

		// For all other cases, use the default behavior
		super.variantChanged(event);
	}

	/**
	 * Shows all swatches.
	 * @param {Event} [event] - The event that triggered the show all swatches.
	 */
	showAllSwatches(event) {
		event?.preventDefault();

		const {overflowList} = this.refs;

		if (overflowList instanceof OverflowList) {
			overflowList.showAll();
		}
	}
}

if (!customElements.get("swatches-variant-picker-component")) {
	customElements.define("swatches-variant-picker-component", SwatchesVariantPickerComponent);
}
